import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Review, ReviewStatus, ModerationStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { formatRelativeTime, getStatusColor, truncate } from '@/lib/utils';
import { Check, X, Eye, ChevronUp, ChevronDown, Undo2, BadgeCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReviewDataTableProps {
  reviews: Review[];
  selectedReviews: string[];
  onSelectionChange: (reviewIds: string[]) => void;
  onApprove: (reviewId: string) => void;
  onReject: (reviewId: string) => void;
  onUnapprove: (reviewId: string) => void;
  onViewDetail: (reviewId: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  canEdit?: boolean;
}

const SORTABLE_COLUMNS = ['createdAt', 'rating'];
const CONTENT_TITLE_MAX = 50;
const CONTENT_TEXT_MAX = 80;

export function ReviewDataTable({
  reviews,
  selectedReviews,
  onSelectionChange,
  onApprove,
  onReject,
  onUnapprove,
  onViewDetail,
  sortBy,
  sortOrder = 'desc',
  onSort,
  canEdit = true,
}: ReviewDataTableProps) {
  const { t } = useTranslation('reviews');
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());
  const [pendingReject, setPendingReject] = useState<string | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(reviews.map((r) => r.reviewId));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (reviewId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedReviews, reviewId]);
    } else {
      onSelectionChange(selectedReviews.filter((id) => id !== reviewId));
    }
  };

  const handleSort = (column: string) => {
    if (!onSort) return;

    if (sortBy === column) {
      onSort(column, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(column, 'desc');
    }
  };

  const toggleContentExpand = (reviewId: string) => {
    setExpandedContent((prev) => {
      const next = new Set(prev);
      if (next.has(reviewId)) {
        next.delete(reviewId);
      } else {
        next.add(reviewId);
      }
      return next;
    });
  };

  const handleConfirmReject = () => {
    if (!pendingReject) return;
    onReject(pendingReject);
    setPendingReject(null);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (!SORTABLE_COLUMNS.includes(column)) return null;

    if (sortBy === column) {
      return sortOrder === 'asc' ? (
        <ChevronUp className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      );
    }

    return <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-300" />;
  };

  const allSelected = reviews.length > 0 && selectedReviews.length === reviews.length;
  const someSelected = selectedReviews.length > 0 && selectedReviews.length < reviews.length;

  const stickyTh = 'sticky top-0 bg-background z-10';

  return (
    <>
      <div className="border rounded-lg max-h-[65vh] overflow-auto">
        <Table wrapperClassName="overflow-visible">
          <TableHeader>
            <TableRow>
              <TableHead className={cn(stickyTh, 'w-12')}>
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label={t('table.selectAll')}
                  className={cn(someSelected && 'data-[state=checked]:bg-primary/50')}
                />
              </TableHead>
              <TableHead
                className={cn(stickyTh, 'cursor-pointer hover:bg-muted/50')}
                onClick={() => handleSort('createdAt')}
              >
                <span className="flex items-center gap-1 whitespace-nowrap">
                  {t('table.headers.date')} <SortIcon column="createdAt" />
                </span>
              </TableHead>
              <TableHead className={stickyTh}>{t('table.headers.sku')}</TableHead>
              <TableHead className={stickyTh}>{t('table.headers.product')}</TableHead>
              <TableHead className={stickyTh}>{t('table.headers.user')}</TableHead>
              <TableHead
                className={cn(stickyTh, 'cursor-pointer hover:bg-muted/50')}
                onClick={() => handleSort('rating')}
              >
                <span className="flex items-center gap-1 whitespace-nowrap">
                  {t('table.headers.rating')} <SortIcon column="rating" />
                </span>
              </TableHead>
              <TableHead className={stickyTh}>{t('table.headers.content')}</TableHead>
              <TableHead className={stickyTh}>{t('table.headers.status')}</TableHead>
              <TableHead className={stickyTh}>{t('table.headers.moderation')}</TableHead>
              <TableHead className={cn(stickyTh, 'text-right')}>{t('table.headers.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                  {t('table.noReviews')}
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => {
                const isExpanded = expandedContent.has(review.reviewId);
                const needsExpand =
                  review.title.length > CONTENT_TITLE_MAX || review.content.length > CONTENT_TEXT_MAX;
                const fullProductName = `${review.productName}${review.productVintage ? ` ${review.productVintage}` : ''}`;
                const isRevoked = review.status === ReviewStatus.Pending && review.autoApproved === true;

                return (
                  <TableRow
                    key={review.reviewId}
                    className={cn(
                      review.moderationStatus === ModerationStatus.PreFlagged &&
                        'bg-orange-50 hover:bg-orange-100',
                      review.moderationStatus === ModerationStatus.Flagged &&
                        'bg-red-50 hover:bg-red-100',
                      selectedReviews.includes(review.reviewId) && 'bg-blue-50'
                    )}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedReviews.includes(review.reviewId)}
                        onCheckedChange={(checked) =>
                          handleSelectOne(review.reviewId, checked as boolean)
                        }
                        aria-label={t('table.selectReview', { id: review.reviewId })}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(review.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground font-mono">
                      {review.productSku}
                    </TableCell>
                    <TableCell className="font-medium">
                      <span title={fullProductName}>
                        {truncate(review.productName, 30)}
                        {review.productVintage && ` ${review.productVintage}`}
                      </span>
                    </TableCell>
                    <TableCell>{review.userName}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span
                            key={i}
                            className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="text-sm">
                        <div className="font-medium">
                          {isExpanded ? review.title : truncate(review.title, CONTENT_TITLE_MAX)}
                        </div>
                        <div className="text-muted-foreground">
                          {isExpanded ? review.content : truncate(review.content, CONTENT_TEXT_MAX)}
                        </div>
                        {needsExpand && (
                          <button
                            onClick={() => toggleContentExpand(review.reviewId)}
                            className="text-xs text-primary hover:underline mt-1"
                          >
                            {isExpanded ? t('table.showLess') : t('table.showMore')}
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge className={cn(getStatusColor(review.status), 'text-[11px]')}>
                          {t(`filters.reviewStatusValues.${review.status}`)}
                        </Badge>
                        {review.verified && (
                          <span title={t('detail.verifiedOrder')}>
                            <BadgeCheck className="h-4 w-4 text-blue-600 shrink-0" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className={cn('text-xs', isRevoked && 'text-red-600 font-medium')}>
                          {isRevoked
                            ? t('moderation.autoApprovalRevoked')
                            : review.autoApproved
                            ? t('moderation.autoApproved')
                            : review.moderationStatus === ModerationStatus.PreFlagged
                            ? t('moderation.manualReviewRequired')
                            : ''}
                        </span>
                        <span className={cn(
                          'text-xs',
                          review.moderationStatus === ModerationStatus.PreFlagged
                            ? 'text-orange-600'
                            : 'text-muted-foreground'
                        )}>
                          {review.moderationStatus === ModerationStatus.PreFlagged
                            ? t('moderation.wordFilterHit')
                            : t('moderation.wordFilterClean')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {review.status === ReviewStatus.Pending && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onApprove(review.reviewId)}
                              disabled={!canEdit}
                              className={cn(
                                'h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50',
                                !canEdit && 'opacity-40 pointer-events-none'
                              )}
                              title={t('table.tooltips.approve')}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPendingReject(review.reviewId)}
                              disabled={!canEdit}
                              className={cn(
                                'h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50',
                                !canEdit && 'opacity-40 pointer-events-none'
                              )}
                              title={t('table.tooltips.reject')}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {review.status === ReviewStatus.Approved && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onUnapprove(review.reviewId)}
                            disabled={!canEdit}
                            className={cn(
                              'h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50',
                              !canEdit && 'opacity-40 pointer-events-none'
                            )}
                            title={t('actions.unapprove')}
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onViewDetail(review.reviewId)}
                          className="h-8 w-8 p-0"
                          title={t('table.tooltips.viewDetail')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <ConfirmationDialog
        open={!!pendingReject}
        onOpenChange={(open) => { if (!open) setPendingReject(null); }}
        title={t('confirm.rejectTitle')}
        description={t('confirm.rejectDescription')}
        confirmLabel={t('confirm.confirm')}
        cancelLabel={t('confirm.cancel')}
        variant="destructive"
        onConfirm={handleConfirmReject}
      />
    </>
  );
}
